import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PersonalInfoStep, { PersonalInfoForm } from './steps/personalInfoStep';
import DocumentStep from './steps/documentStep';
import { kycService } from 'api/kyc';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { updateUserProfile } from 'store/authSlice';
import { showToast } from 'store/toastSlice';
import { UserProfile } from 'types/auth';
import styles from './verificationTab.module.scss';

type Tab = 'General' | 'Security' | 'Sessions' | 'Verification';

interface VerificationTabProps {
  onNavigate?: (tab: Tab) => void;
}

const DEFAULT_PROFILE: UserProfile = {
  firstName: null,
  lastName: null,
  dateOfBirth: null,
  country: null,
  state: null,
  city: null,
  postalCode: null,
  streetAddress: null,
};

const parseDateOfBirth = (dob?: string | null) => {
  if (!dob) return { birthDay: '', birthMonth: '', birthYear: '' };

  const dateOnly = dob.split('T')[0];
  const parts = dateOnly.split('-');

  if (parts.length === 3) {
    const [year, month, day] = parts;
    const numMonth = Number(month);
    const numDay = Number(day);

    return {
      birthYear: year || '',
      birthMonth: !isNaN(numMonth) ? String(numMonth) : '',
      birthDay: !isNaN(numDay) ? String(numDay) : '',
    };
  }

  return { birthDay: '', birthMonth: '', birthYear: '' };
};

const mapProfileToForm = (profile?: UserProfile | null): PersonalInfoForm => {
  const p = profile || DEFAULT_PROFILE;
  const { birthDay, birthMonth, birthYear } = parseDateOfBirth(p.dateOfBirth);
  return {
    firstName: p.firstName || '',
    lastName: p.lastName || '',
    birthDay,
    birthMonth,
    birthYear,
    streetAddress: p.streetAddress || '',
    city: p.city || '',
    state: p.state || '',
    country: p.country || '',
    postalCode: p.postalCode || '',
  };
};

const isProfileComplete = (user?: { isProfileVerified?: boolean } | null): boolean => {
  return Boolean(user?.isProfileVerified);
};

/** Three selects in, one ISO `YYYY-MM-DD` out — the shape the backend stores. */
const toIsoDate = (day: string, month: string, year: string) =>
  `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

const VerificationTab: React.FC<VerificationTabProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState<PersonalInfoForm>(() => mapProfileToForm(user?.profile));
  const [isComplete, setIsComplete] = useState<boolean>(() => isProfileComplete(user));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user?.profile) {
      setForm(mapProfileToForm(user.profile));
    }
    setIsComplete(isProfileComplete(user));
  }, [user?.profile, user?.isProfileVerified]);

  const handleChange = (field: keyof PersonalInfoForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const res = await kycService.submitPersonalInfo({
        firstName: form.firstName,
        lastName: form.lastName,
        dateOfBirth: toIsoDate(form.birthDay, form.birthMonth, form.birthYear),
        country: form.country,
        state: form.state,
        city: form.city,
        postalCode: form.postalCode,
        streetAddress: form.streetAddress,
      });

      const isVerified = Boolean(res?.data?.isProfileVerified ?? true);
      setIsComplete(isVerified);

      dispatch(
        updateUserProfile({
          profile: {
            firstName: form.firstName,
            lastName: form.lastName,
            dateOfBirth: toIsoDate(form.birthDay, form.birthMonth, form.birthYear),
            country: form.country,
            state: form.state,
            city: form.city,
            postalCode: form.postalCode,
            streetAddress: form.streetAddress,
          },
          isProfileVerified: isVerified,
        }),
      );

      dispatch(
        showToast({
          text: res?.data?.message || t('settings.verification.toasts.profileUpdated'),
          type: 'success',
        }),
      );
    } catch (error: any) {
      const message = error?.response?.data?.message || t('settings.verification.toasts.submitFailed');
      dispatch(showToast({ text: message, type: 'error' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.verificationTab}>
      {step === 1 ? (
        <PersonalInfoStep
          values={form}
          isComplete={isComplete}
          isSubmitting={isSubmitting}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onGoToStep2={() => setStep(2)}
        />
      ) : (
        <DocumentStep onBack={() => setStep(1)} isKycVerified={user?.isKycVerified ?? false} />
      )}

      <div className={styles.securityNote}>
        <div className={styles.noteIcon}>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className={styles.noteContent}>
          <h4>{t('settings.verification.securityNoteTitle')}</h4>
          <p>
            {t('settings.verification.securityNoteDesc')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerificationTab;
